'use client';

import Link from 'next/link';
import Image from 'next/image';

import { Dumbbell } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';

import { useLocalized } from '~/(tenant)/_components/native/_lib/use-localized';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  ClassStatusBadge,
  HorizontalCardScroller,
  SectionHeader,
} from '~/(tenant)/_components/native/_primitives';

import type { BookingCategorySectionConfig } from '~/(tenant)/_lib/schema/native-home-config.schema';

interface BookingCategorySectionProps {
  config: BookingCategorySectionConfig;
  accountSlug: string;
  /** UUID of the active venue from the location-switcher cookie. When set,
   *  the RPC scopes to sessions whose court belongs to this location. */
  locationId?: string | null;
}

interface ClassSession {
  booking_id: string;
  class_name: string;
  image_url: string | null;
  trainer_name: string | null;
  start_time: string;
  end_time: string;
  booking_date: string;
  max_capacity: number;
  enrolled_count: number;
  spots_remaining: number;
  is_full: boolean;
  waitlist_enabled: boolean;
  show_spots_remaining: boolean;
  show_spots_remaining_last_only: boolean;
}

export function BookingCategorySection({
  config,
  accountSlug,
  locationId,
}: BookingCategorySectionProps) {
  const t = useNativeTokens();
  const client = useSupabase();
  const today = new Date().toISOString().split('T')[0]!;
  const endDate = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]!;

  const { data: sessions, isLoading } = useQuery({
    queryKey: [
      'native-home-category',
      accountSlug,
      config.sportSlug,
      config.categorySlug,
      today,
      locationId ?? 'all',
    ],
    queryFn: async () => {
      const { data } = await client.rpc('get_available_classes_for_date', {
        p_account_slug: accountSlug,
        p_date: today,
        p_end_date: endDate,
        p_category: config.categorySlug,
        p_location_id: locationId ?? undefined,
      });
      return ((data ?? []) as ClassSession[]).slice(0, 10);
    },
    staleTime: 60_000,
  });

  const localizedLabel = useLocalized(config.label, config.label_ar);
  const displayLabel = localizedLabel ?? config.categorySlug.replace(/_/g, ' ');

  // The builder stores the raw class_types.category value (which may be
  // "Kids Padel Academy" rather than "kids_padel_academy"). The booking-type
  // ?type= lock compares against class_category_slugs, which the RPCs normalize
  // as lower(trim(category)) with spaces → underscores. Normalize the same way
  // so the deep link's type matches and the sport/category lock engages.
  const categoryTypeSlug = config.categorySlug.trim().toLowerCase().replace(/\s+/g, '_');

  if (isLoading) {
    return (
      <section style={{ paddingTop: 32 }}>
        <SectionHeader title={displayLabel} />
        <div style={{ display: 'flex', gap: 14, paddingLeft: 22, paddingRight: 22, marginTop: 16, overflow: 'hidden' }}>
          {[0, 1].map((i) => (
            <div
              key={i}
              style={{
                height: 260,
                width: 220,
                flexShrink: 0,
                borderRadius: t.radius,
                backgroundColor: t.line,
              }}
            />
          ))}
        </div>
      </section>
    );
  }

  if (!sessions || sessions.length === 0) return null;

  return (
    <section style={{ paddingTop: 32 }}>
      <SectionHeader
        title={displayLabel}
        action={{
          label: <Trans i18nKey="common:seeAll" defaults="All" />,
          // Service-level deep link: lock the sport + this category so "View
          // all" lands directly in the category's class list (toggle + packages
          // hidden). Matches the "Book by sport" quick-action/hero links. The
          // old `?tab=classes&category=` form was ignored by /book (page.tsx
          // only reads ?activity= and ?type=), so it dropped onto the generic
          // booking page instead of this category.
          href: config.sportSlug
            ? `/book?activity=${config.sportSlug}&type=${categoryTypeSlug}`
            : `/book?type=${categoryTypeSlug}`,
        }}
      />

      <HorizontalCardScroller>
        {sessions.map((session) => (
          <SessionCard key={session.booking_id} session={session} />
        ))}
      </HorizontalCardScroller>
    </section>
  );
}

function SessionCard({ session }: { session: ClassSession }) {
  const t = useNativeTokens();
  const { t: translate, i18n } = useTranslation();
  const bookingDate = new Date(session.booking_date + 'T00:00:00');
  const now = new Date();
  const isToday = bookingDate.toDateString() === now.toDateString();
  const isTomorrow = bookingDate.toDateString() === new Date(now.getTime() + 86400000).toDateString();

  const dateLabel = isToday
    ? translate('common:nativeHome.dateLabel.today')
    : isTomorrow
      ? translate('common:nativeHome.dateLabel.tomorrow')
      : formatLocaleDate(bookingDate, i18n.language, { weekday: 'short', day: undefined, month: undefined, year: undefined });

  return (
    <Link
      href={`/event/${session.booking_id}`}
      style={{
        flexShrink: 0,
        width: 220,
        textDecoration: 'none',
        color: t.foreground,
        scrollSnapAlign: 'start',
      }}
    >
      <div
        style={{
          position: 'relative',
          height: 260,
          borderRadius: t.radius,
          overflow: 'hidden',
          marginBottom: 12,
          backgroundColor: t.line,
        }}
      >
        {session.image_url ? (
          <Image src={session.image_url} alt={session.class_name} fill className="object-cover" unoptimized />
        ) : (
          <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: t.muted }}>
            <Dumbbell size={36} strokeWidth={1.2} />
          </div>
        )}

        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.6))',
          }}
        />

        <ClassStatusBadge
          spotsRemaining={session.spots_remaining}
          isFull={session.is_full}
          waitlistEnabled={session.waitlist_enabled}
          showSpotsRemaining={session.show_spots_remaining}
          lastSpotOnly={session.show_spots_remaining_last_only}
        />


        <div style={{ position: 'absolute', bottom: 14, left: 14, right: 14, color: '#fff' }}>
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 8,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              opacity: 0.85,
              marginBottom: 4,
            }}
          >
            {dateLabel} · {formatTimeToAmPm(session.start_time, i18n.language)}
          </div>
          <div style={{ fontFamily: t.fontDisplay, fontSize: 20, fontWeight: 400, lineHeight: 1.15 }}>
            {session.class_name}
          </div>
        </div>
      </div>

      {session.trainer_name && (
        <div
          style={{
            fontFamily: t.fontSans,
            fontSize: 11,
            color: t.muted,
          }}
        >
          {translate('common:nativeHome.withTrainer', { name: session.trainer_name })}
        </div>
      )}
    </Link>
  );
}
